import { useEffect, useRef, useState } from "react";
import { Truck, Save, X, Building2, User, Phone, Mail } from "lucide-react";
import Swal from "sweetalert2";
import api from "../services/api";

export default function SupplierForm({ onSaved, editingSupplier, onCancel }) {
  const nameRef = useRef(null);

  const [form, setForm] = useState({
    nombre: "",
    nombre_comercial: "",
    contacto: "",
    telefono: "",
    email: "",
    direccion: "",
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingSupplier) {
      setForm({
        nombre: editingSupplier.nombre || "",
        nombre_comercial: editingSupplier.nombre_comercial || "",
        contacto: editingSupplier.contacto || "",
        telefono: editingSupplier.telefono || "",
        email: editingSupplier.email || "",
        direccion: editingSupplier.direccion || "",
      });
    }
  }, [editingSupplier]);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingSupplier) {
        await api.put(`/suppliers/${editingSupplier.id}`, form);
        Swal.fire("Updated", "Supplier updated successfully", "success");
      } else {
        await api.post("/suppliers", form);
        Swal.fire("Saved", "Supplier created successfully", "success");
      }

      onSaved();
    } catch (error) {
      if (error.response?.status === 422) {
        const errors = error.response.data.errors;
        const firstError = Object.values(errors)[0][0];
        Swal.fire("Validation Error", firstError, "error");
      } else {
        Swal.fire("Error", "There was a problem saving the supplier", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: "24px" }}>
      <div className="card-header">
        <div className="d-flex align-items-center gap-2">
          <Truck size={24} color="#667eea" />
          <h2 className="card-title" style={{ fontSize: "20px" }}>
            {editingSupplier ? "Edit Supplier" : "New Supplier"}
          </h2>
        </div>
      </div>

      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <h4
            style={{
              fontSize: "16px",
              fontWeight: 600,
              marginBottom: "16px",
              color: "#2d3748",
            }}
          >
            <Building2 size={18} style={{ display: "inline", marginRight: "8px" }} />
            General Information
          </h4>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                type="text"
                name="nombre"
                className="form-control"
                placeholder="Supplier name"
                value={form.nombre}
                onChange={handleChange}
                ref={nameRef}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Business Name</label>
              <input
                type="text"
                name="nombre_comercial"
                className="form-control"
                placeholder="Trade name"
                value={form.nombre_comercial}
                onChange={handleChange}
              />
            </div>
          </div>

          <div
            style={{
              borderTop: "2px solid #e2e8f0",
              margin: "24px 0",
            }}
          ></div>

          <h4
            style={{
              fontSize: "16px",
              fontWeight: 600,
              marginBottom: "16px",
              color: "#2d3748",
            }}
          >
            <User size={18} style={{ display: "inline", marginRight: "8px" }} />
            Contact Information
          </h4>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            <div className="form-group">
              <label className="form-label">
                <User size={14} style={{ display: "inline", marginRight: "4px" }} />
                Contact Person
              </label>
              <input
                type="text"
                name="contacto"
                className="form-control"
                placeholder="Contact name"
                value={form.contacto}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <Phone size={14} style={{ display: "inline", marginRight: "4px" }} />
                Phone
              </label>
              <input
                type="text"
                name="telefono"
                className="form-control"
                placeholder="(000) 000-0000"
                value={form.telefono}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                <Mail size={14} style={{ display: "inline", marginRight: "4px" }} />
                Email
              </label>
              <input
                type="email"
                name="email"
                className="form-control"
                placeholder="email@example.com"
                value={form.email}
                onChange={handleChange}
              />
            </div>
          </div>

          <div
            style={{
              borderTop: "2px solid #e2e8f0",
              margin: "24px 0",
            }}
          ></div>

          <h4
            style={{
              fontSize: "16px",
              fontWeight: 600,
              marginBottom: "16px",
              color: "#2d3748",
            }}
          >
            Address
          </h4>

          <div className="form-group" style={{ marginBottom: "24px" }}>
            <label className="form-label">Full Address</label>
            <input
              type="text"
              name="direccion"
              className="form-control"
              placeholder="Supplier address"
              value={form.direccion}
              onChange={handleChange}
            />
          </div>

          <div className="d-flex gap-2" style={{ marginTop: "32px" }}>
            <button type="submit" className="btn btn-success" disabled={loading}>
              <Save size={18} />
              {loading ? "Saving..." : editingSupplier ? "Update Supplier" : "Save Supplier"}
            </button>

            {onCancel && (
              <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
                <X size={18} />
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}